import React from 'react';
import { format } from 'date-fns';

import { downloadTextFile, sanitizeFileNamePart } from 'common/download';

type UseWidgetDslDownloadParams = {
  dslText: string;
  isDslValid: boolean;
  widgetName?: string;
};

type UseWidgetDslDownloadResult = {
  isInvalidDownloadModalOpen: boolean;
  requestDownloadDsl: () => void;
  confirmDownloadInvalidDsl: () => void;
  cancelDownloadInvalidDsl: () => void;
};

const WIDGET_DSL_DOWNLOAD_MIME_TYPE = 'application/json;charset=utf-8';
const WIDGET_DSL_DOWNLOAD_FALLBACK_NAME = 'widget-dsl';

export function useWidgetDslDownload({
  dslText,
  isDslValid,
  widgetName,
}: UseWidgetDslDownloadParams): UseWidgetDslDownloadResult {
  const [isInvalidDownloadModalOpen, setIsInvalidDownloadModalOpen] = React.useState(false);

  const requestDownloadDsl = React.useCallback((): void => {
    if (isDslValid) {
      downloadCurrentDslText(dslText, widgetName);
      return;
    }

    setIsInvalidDownloadModalOpen(true);
  }, [dslText, isDslValid, widgetName]);

  const confirmDownloadInvalidDsl = React.useCallback((): void => {
    downloadCurrentDslText(dslText, widgetName);
    setIsInvalidDownloadModalOpen(false);
  }, [dslText, widgetName]);

  const cancelDownloadInvalidDsl = React.useCallback((): void => {
    setIsInvalidDownloadModalOpen(false);
  }, []);

  return {
    isInvalidDownloadModalOpen,
    requestDownloadDsl,
    confirmDownloadInvalidDsl,
    cancelDownloadInvalidDsl,
  };
}

function downloadCurrentDslText(dslText: string, widgetName?: string): void {
  const fileName = getWidgetDslDownloadFileName(widgetName);

  downloadTextFile(fileName, dslText, WIDGET_DSL_DOWNLOAD_MIME_TYPE);
}

function getWidgetDslDownloadFileName(widgetName?: string, now: Date = new Date()): string {
  const name = sanitizeFileNamePart(widgetName ?? '') || WIDGET_DSL_DOWNLOAD_FALLBACK_NAME;
  const timestamp = sanitizeFileNamePart(`${format(now, 'yyyy-MM-dd')} ${format(now, 'HH-mm-ss')}`);

  return `${timestamp} ${name}.json`;
}
